import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as Yup from "yup";
import { Notify, MainTab } from "components/core";
import { Config } from "./Config";
import { Filters } from "./Filters";
import { Info } from "./Info";
import { Access } from "./Access";
import { Layout } from "./Layout";
import { Widgets } from "./Widgets";

import styles from "./ConfigLayoutDashboards.module.css";
import { useRecoilState, useSetRecoilState } from "recoil";
import {
  currentDashboardState,
  dashboardsState,
  Dashboard,
  newItemState,
  dashboardRolesState,
  DashboardStatusValue,
} from "states";
import { useDashboardService } from "services";
import {
  DASHBOARD_TAB_FIELD_MAP as TAB_FIELD_MAP,
  DASHBOARD_TAB_NAMES as TAB_NAMES,
  DASHBOARD_TEMPLATE_TAB_LABEL as TEMPLATE_TAB_LABEL,
  DASHBOARD_TEMPLATE_KEYS as TEMPLATE_KEYS,
  DASHBOARD_STARTER_TEMPLATES,
  DASHBOARD_TITLE_MAX_LENGTH,
  DASHBOARD_DESCRIPTION_MAX_LENGTH,
  DASHBOARD_PROFILE,
} from "constants/appConstants";
import { DashboardFormHeader } from "components/core/headings/DashboardFormHeader";
import { ValidationErrorBar } from "components/core/bars/ValidationErrorBar";
import { TemplateWarningBar } from "components/core/bars/TemplateWarningBar";
import { SaveAsNewPanel } from "molecules/dashboardEditor/SaveAsNewPanel";
import { DashboardCopy, suggestCopyId, suggestCopyTitle } from "helpers/dashboardCopy";
import { dashboardIdSchema, dashboardTitleSchema } from "helpers/dashboardValidation";
import { isValidJsonOrEmpty, isTemplateUnedited, isAtCharLimit, buildErrorSummary, translateTabName } from "helpers";

interface IFormValues {
  title: string;
  description: string;
  dashboardId: string;
  layoutConfig: string;
  visualConfig: string;
  showDateFilter: boolean;
  dashboardConfig: string;
  widgetsConfig: string;
  accessConfig: any;
  status: DashboardStatusValue;
}

const isStarterTemplate = (key: string, value: string): boolean =>
  isTemplateUnedited(value, DASHBOARD_STARTER_TEMPLATES[key] || "");

const computeTabsWithErrors = (
  errors: Record<string, unknown>,
  saveAttempted: boolean,
  hasAccess: boolean,
): number[] => {
  const tabsWithErrors = Object.entries(TAB_FIELD_MAP).reduce<number[]>((acc, [tabIdx, fields]) => {
    const hasError = fields.some((f) => !!errors[f]);
    if (hasError) acc.push(Number(tabIdx));
    return acc;
  }, []);

  return saveAttempted && !hasAccess ? Array.from(new Set(tabsWithErrors.concat(5))) : tabsWithErrors;
};

const computeIsSaveDisabled = (
  isSubmitting: boolean,
  isTitleAtLimit: boolean,
  isDescriptionAtLimit: boolean,
  saveAttempted: boolean,
  isValid: boolean,
  hasAccess: boolean,
): boolean =>
  isSubmitting || isTitleAtLimit || isDescriptionAtLimit || (saveAttempted && (!isValid || !hasAccess));

const shouldShowValidationBar = (
  saveAttempted: boolean,
  isValid: boolean,
  hasAccess: boolean,
  showTemplateConfirm: boolean,
): boolean => saveAttempted && (!isValid || !hasAccess) && !showTemplateConfirm;

const DASHBOARD_ERROR_FIELD_CHECKS = [
  { field: "title" as const,           tabLabel: "Info" },
  { field: "description" as const,     tabLabel: "Info" },
  { field: "dashboardId" as const,     tabLabel: "Info" },
  { field: "visualConfig" as const,    tabLabel: "Filters" },
  { field: "dashboardConfig" as const, tabLabel: "Config" },
  { field: "widgetsConfig" as const,   tabLabel: "Widgets" },
];

export const ConfigLayoutDashboards = () => {
  const { t } = useTranslation();
  const [tabItems, setTabItems]               = useState<any>([]);
  const [saveAttempted, setSaveAttempted]     = useState(false);
  const [hasPendingVizDelete, setHasPendingVizDelete] = useState(false);
  const [templateWarnTabs, setTemplateWarnTabs] = useState<string[]>([]);
  const [showTemplateConfirm, setShowTemplateConfirm] = useState(false);
  const [confirmedValues, setConfirmedValues] = useState<IFormValues | null>(null);
  // The form as it stood when "Save as new" was pressed; the panel asking for the copy's title and ID is open while this is set.
  const [copyValues, setCopyValues] = useState<IFormValues | null>(null);
  // A copy is being saved. The ref blocks a second click before the state has re-rendered; both are cleared when the request ends
  // (React 18 does not warn about a state update after unmount, so there is no mounted check to get wrong under StrictMode).
  const [savingCopy, setSavingCopy] = useState(false);
  const copyInFlight = useRef(false);
  // Any save (Save, Enter, Save anyway, Save as new) is running. RHF only counts the validation as submitting, not the request, so this blocks a second one.
  const saveInFlight = useRef(false);

  const [currentDashboard, setCurrentDashboard] = useRecoilState(currentDashboardState);
  const [dashboards, setDashboards]             = useRecoilState(dashboardsState);
  const setIsNewItem                            = useSetRecoilState(newItemState);
  const dashboardService                        = useDashboardService();
  const [dashboardRoles, setDashboardRoles]     = useRecoilState(dashboardRolesState);

  const isExisting = !!currentDashboard.id;

  // A plain `string` local — chaining t() directly into Yup's .max() overload resolution trips a
  // TS "excessively deep" instantiation error under this project's TypeScript version.
  const descriptionMaxLengthMessage: string = t('VALIDATION.MAX_LENGTH', { n: DASHBOARD_DESCRIPTION_MAX_LENGTH });

  const validationSchema = Yup.object().shape({
    title: dashboardTitleSchema(t, DASHBOARD_TITLE_MAX_LENGTH),
    description: Yup.string()
      .max(DASHBOARD_DESCRIPTION_MAX_LENGTH, descriptionMaxLengthMessage),
    // The dashboard being edited does not clash with its own ID.
    dashboardId: dashboardIdSchema(dashboards, t, isExisting ? currentDashboard.id : undefined),
    layoutConfig:    Yup.string(),
    visualConfig:    isValidJsonOrEmpty("Global filters", t),
    showDateFilter:  Yup.boolean(),
    dashboardConfig: isValidJsonOrEmpty("Dashboard config", t),
    widgetsConfig:   isValidJsonOrEmpty("Widgets config", t),
    status:          Yup.string().oneOf(["LIVE", "TEST"]),
  });

  const dashboardData = currentDashboard.data[0];

  const { register, handleSubmit, formState, reset, setValue, trigger, watch } =
    useForm<IFormValues>({
      resolver: yupResolver(validationSchema),
      defaultValues: {
        title:           currentDashboard.title || "",
        description:     dashboardData.info.description || "",
        dashboardId:     currentDashboard.dashboardId || "",
        layoutConfig:    dashboardData.layout.layoutConfig || "",
        visualConfig:    dashboardData.filters.visualConfig || "",
        showDateFilter:  dashboardData.filters.showDateFilter || false,
        dashboardConfig: dashboardData.config.dashboardConfig || "",
        widgetsConfig:   dashboardData.widgets.widgetsConfig || "",
        accessConfig:    dashboardData.access.accessConfig || dashboardData.access.escapedAccessConfig,
        status:          currentDashboard.status ?? "TEST",
      },
      mode: "all",
    });

  const { errors, isSubmitting, isValid } = formState;
  const titleValue = watch("title");
  const descriptionValue = watch("description");
  const isTitleAtLimit = isAtCharLimit(titleValue, DASHBOARD_TITLE_MAX_LENGTH);
  const isDescriptionAtLimit = isAtCharLimit(descriptionValue, DASHBOARD_DESCRIPTION_MAX_LENGTH);

  // ── Tabs with hard RHF errors ───────────────────────────────────────────────
  const allTabsWithErrors: number[] = computeTabsWithErrors(errors, saveAttempted, dashboardRoles.length > 0);

  // Readable tab names for the error bar
  const errorTabNames = allTabsWithErrors.map((i) => TAB_NAMES[i]);

  // ── Tab label with dot indicator ────────────────────────────────────────────
  // `name` stays the canonical English key for comparisons (templateWarnTabs
  // etc.) — only the rendered text is translated.
  const tabLabel = (name: string, idx: number) => {
    const hasHardError    = allTabsWithErrors.includes(idx);
    const hasTemplateWarn = !isExisting && templateWarnTabs.includes(name);
    const displayName = translateTabName(name, t);
    if (!hasHardError && !hasTemplateWarn) return displayName;
    return (
      <span className={styles.tabLabelWrap}>
        {displayName}
        <span
          title={
            hasHardError
              ? t('VALIDATION.TAB_HAS_ERRORS')
              : t('VALIDATION.TAB_TEMPLATE_NOT_EDITED')
          }
          className={`${styles.tabDot} ${hasHardError ? styles.tabDotError : styles.tabDotWarning}`}
        />
      </span>
    );
  };

  const handleVisualizationDeleted = useCallback(() => setHasPendingVizDelete(true), []);

  const buildTabItems = () => [
    { id: 0, name: tabLabel("Info", 0),    target: "#nav-info",    control: "nav-info",    tabId: "nav-info-tab",
      content: <Info    register={register} setValue={setValue} formState={formState} isExisting={isExisting}
                        showStatus={isExisting && !!currentDashboard.status}
                        titleMaxLength={DASHBOARD_TITLE_MAX_LENGTH} descriptionMaxLength={DASHBOARD_DESCRIPTION_MAX_LENGTH}
                        titleValue={titleValue} descriptionValue={descriptionValue} /> },
    { id: 1, name: tabLabel("Layout", 1),  target: "#nav-layout",  control: "nav-layout",  tabId: "nav-layout-tab",
      content: <Layout  register={register} setValue={setValue} status={false} onVisualizationDeleted={handleVisualizationDeleted} /> },
    { id: 2, name: tabLabel("Filters", 2), target: "#nav-filters", control: "nav-filters", tabId: "nav-filters-tab",
      content: <Filters register={register} setValue={setValue} formState={formState} isExisting={isExisting} /> },
    { id: 3, name: tabLabel("Config", 3),  target: "#nav-config",  control: "nav-config",  tabId: "nav-config-tab",
      content: <Config  register={register} setValue={setValue} formState={formState} isExisting={isExisting} /> },
    { id: 4, name: tabLabel("Widgets", 4), target: "#nav-widgets", control: "nav-widgets", tabId: "nav-widgets-tab",
      content: <Widgets register={register} setValue={setValue} formState={formState} isExisting={isExisting} /> },
    { id: 5, name: tabLabel("Access", 5),  target: "#nav-access",  control: "nav-access",  tabId: "nav-access-tab",
      content: <Access  register={register} setValue={setValue} formState={formState} saveAttempted={saveAttempted} /> },
  ];

  useEffect(() => {
    setTabItems(buildTabItems());
  }, [
    errors.title, errors.description, errors.dashboardId, errors.visualConfig,
    errors.dashboardConfig, errors.widgetsConfig,
    titleValue, descriptionValue,
    currentDashboard, saveAttempted, dashboardRoles.length,
    templateWarnTabs,
    t,
  ]);

  useEffect(() => {
    setDashboardRoles(currentDashboard.data[0].access.escapedAccessConfig || []);
    reset();
    const d = currentDashboard.data[0];
    setValue("title",           currentDashboard.title || "");
    setValue("description",     d.info.description || "");
    setValue("dashboardId",     currentDashboard.dashboardId || "");
    setValue("layoutConfig",    d.layout.layoutConfig || "");
    setValue("visualConfig",    d.filters.visualConfig || "");
    setValue("showDateFilter",  d.filters.showDateFilter || false);
    setValue("dashboardConfig", d.config.dashboardConfig || "");
    setValue("widgetsConfig",   d.widgets.widgetsConfig || "");
    setValue("accessConfig",    d.access.accessConfig || d.access.escapedAccessConfig);
    setValue("status",          currentDashboard.status ?? "TEST");
    setSaveAttempted(false);
    setShowTemplateConfirm(false);
    setConfirmedValues(null);
    setCopyValues(null);
    setTemplateWarnTabs([]);
    setHasPendingVizDelete(false);
    trigger();
  }, [currentDashboard, reset, setValue]);

  const handleBack = useCallback(() => {
    const unsavedIdx = dashboards.findIndex((d: Dashboard) => d.id === undefined);
    if (unsavedIdx !== -1) {
      const updated = [...dashboards];
      updated.splice(unsavedIdx, 1);
      setDashboards(updated);
      setIsNewItem(false);
    }
    setCurrentDashboard({} as Dashboard);
    reset();
  }, [dashboards, setDashboards, setIsNewItem, setCurrentDashboard, reset]);

  const notifyValidationErrors = useCallback((currentErrors: typeof errors) => {
    const hasAccess = dashboardRoles.length > 0;
    Notify.error(buildErrorSummary(currentErrors, DASHBOARD_ERROR_FIELD_CHECKS, [
      { label: "Access", message: t('VALIDATION.ACCESS_ROLE_REQUIRED'), passed: hasAccess },
    ], t));
  }, [dashboardRoles.length, t]);

  const isSaveDisabled = computeIsSaveDisabled(
    isSubmitting, isTitleAtLimit, isDescriptionAtLimit, saveAttempted, isValid, dashboardRoles.length > 0,
  );

  // `copy` saves the form as a brand-new dashboard under that title and ID, leaving the one being edited as it was.
  const onSubmit = useCallback((values: IFormValues, copy?: DashboardCopy): Promise<void> => {
    if (saveInFlight.current) return Promise.resolve();
    saveInFlight.current = true;
    const {
      description, layoutConfig,
      visualConfig, showDateFilter, dashboardConfig, widgetsConfig, status,
    } = values;
    const title = copy?.title ?? values.title;
    const dashboardId = copy?.dashboardId ?? values.dashboardId;
    const updating = isExisting && !copy;

    const temp: any[] = [];
    try {
      JSON.parse(layoutConfig).forEach((item: { cols: any[] }) => {
        item.cols.forEach((entry: any) => {
          if (entry.visualizationCode) temp.push(entry.visualizationCode);
        });
      });
    } catch {
      // malformed layout config — nothing to collect
    }

    const dashboardDetail: any = {
      title, isPinned: false, type: "dashboard", dashboardId,
      data: [{
        info:    { title, description },
        layout:  { layoutConfig },
        filters: { visualConfig, showDateFilter },
        config:  { dashboardConfig },
        widgets: { widgetsConfig },
        access:  {
          escapedAccessConfig: dashboardRoles,
          accessConfig: JSON.stringify(dashboardRoles),
        },
      }],
      profile: DASHBOARD_PROFILE,
      visualizationPermission: temp,
    };

    if (updating) {
      dashboardDetail.id = currentDashboard.id;
      // Only an existing dashboard whose API reports a status carries one; a new one (or a copy) gets the environment default.
      if (currentDashboard.status) dashboardDetail.status = status;
    }

    return dashboardService.createOrUpdateDashboard(dashboardDetail).then(
      () => {
        if (updating && hasPendingVizDelete) {
          Notify.success(t('TOAST.DELETE_SUCCESS'));
        }
        Notify.success(updating ? t('TOAST.UPDATE_SUCCESS') : t('TOAST.CREATE_SUCCESS'));
        setHasPendingVizDelete(false);
        setIsNewItem(false);
        dashboardService.getDashboards().then(
          (all: Dashboard[]) => setDashboards(all),
          (err: any) => Notify.error(err?.message || err?.statusInfo?.errorMessage || err || t('TOAST.ERROR_GENERIC'))
        );
        setCurrentDashboard({} as Dashboard);
        reset();
        setSaveAttempted(false);
        setTemplateWarnTabs([]);
        setShowTemplateConfirm(false);
        setConfirmedValues(null);
        setCopyValues(null);
      },
      (error) => {
        Notify.error(error?.message || error?.statusInfo?.errorMessage || error || t('TOAST.ERROR_GENERIC'));
      }
    )
      .finally(() => {
        saveInFlight.current = false;
      });
  }, [dashboardRoles, currentDashboard, isExisting, hasPendingVizDelete, dashboardService,
    setHasPendingVizDelete, setIsNewItem, setDashboards, setCurrentDashboard, reset,
    setSaveAttempted, setTemplateWarnTabs, setShowTemplateConfirm, setConfirmedValues, t]);

  const handleSaveClick = useCallback(() => {
    setSaveAttempted(true);
    handleSubmit(
      (currentValues) => {
        const hasAccess = dashboardRoles.length > 0;
        if (!hasAccess) {
          notifyValidationErrors({});
          return;
        }

        const warnings = TEMPLATE_KEYS
          .filter((key) => isStarterTemplate(key, currentValues[key as keyof IFormValues] as string))
          .map((key) => TEMPLATE_TAB_LABEL[key]);

        setTemplateWarnTabs(warnings);

        if (warnings.length > 0 && !isExisting) {
          setConfirmedValues(currentValues);
          setShowTemplateConfirm(true);
          return;
        }

        onSubmit(currentValues);
      },
      // RHF passes the freshly-computed errors here, so this can't go stale
      // the way reading `errors` from the render closure could.
      (freshErrors) => notifyValidationErrors(freshErrors)
    )();
  }, [handleSubmit, dashboardRoles.length, notifyValidationErrors, isExisting, onSubmit]);

  // Same checks as Save (a copy of a broken dashboard would be broken too), then ask what to call the copy.
  const handleSaveAsNewClick = useCallback(() => {
    setSaveAttempted(true);
    handleSubmit(
      (currentValues) => {
        if (dashboardRoles.length === 0) {
          notifyValidationErrors({});
          return;
        }
        setCopyValues(currentValues);
      },
      (freshErrors) => notifyValidationErrors(freshErrors)
    )();
  }, [handleSubmit, dashboardRoles.length, notifyValidationErrors]);

  const saveCopy = (copy: DashboardCopy) => {
    if (!copyValues || copyInFlight.current) return;
    copyInFlight.current = true;
    setSavingCopy(true);
    onSubmit(copyValues, copy).finally(() => {
      copyInFlight.current = false;
      setSavingCopy(false);
    });
  };

  const handleConfirmSave = useCallback(() => {
    setShowTemplateConfirm(false);
    if (confirmedValues) onSubmit(confirmedValues);
  }, [confirmedValues, onSubmit]);

  const handleCancelConfirm = useCallback(() => {
    setShowTemplateConfirm(false);
    setConfirmedValues(null);
  }, []);

  return (
    <div className={styles.detailPage}>
      <DashboardFormHeader
        dashboard={currentDashboard}
        isSaveDisabled={isSaveDisabled}
        isSubmitting={isSubmitting || savingCopy}
        onBack={handleBack}
        onSave={handleSaveClick}
        onSaveAsNew={isExisting ? handleSaveAsNewClick : undefined}
      />

      {copyValues && (
        <SaveAsNewPanel
          initialTitle={suggestCopyTitle(copyValues.title, DASHBOARD_TITLE_MAX_LENGTH)}
          initialId={suggestCopyId(copyValues.dashboardId, dashboards)}
          dashboards={dashboards}
          titleMaxLength={DASHBOARD_TITLE_MAX_LENGTH}
          busy={savingCopy}
          onConfirm={saveCopy}
          onCancel={() => setCopyValues(null)}
        />
      )}

      {shouldShowValidationBar(saveAttempted, isValid, dashboardRoles.length > 0, showTemplateConfirm) && (
        <ValidationErrorBar tabsWithErrors={errorTabNames} />
      )}

      {showTemplateConfirm && (
        <TemplateWarningBar
          warnTabs={templateWarnTabs}
          onConfirm={handleConfirmSave}
          onCancel={handleCancelConfirm}
        />
      )}

      <div className={styles.detailBody}>
        <form onSubmit={(e) => { e.preventDefault(); handleSaveClick(); }}>
          <MainTab tabItems={tabItems} />
        </form>
      </div>
    </div>
  );
};